import React, { Suspense, useEffect, useRef, useState } from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Switch, Route, Redirect, matchPath, useLocation } from 'react-router-dom';
import feathers from '@feathersjs/feathers';
import socketio from '@feathersjs/socketio-client';
import io from 'socket.io-client';
import auth from '@feathersjs/authentication-client';
import * as serviceWorker from './serviceWorker';
import { handleError } from './reducers/ErrorReducer';
import { setWebsocketConnection } from './reducers/WebsocketReducer';
import { getRestClient } from './restClient';
import {
  setUserAuthenticated,
  setUserData,
  logOut,
  getUserClearanceLevel,
  submitLoginForm,
} from './reducers/UserReducer';
import { ClimbingBoxLoader } from 'react-spinners';
import { motion } from 'framer-motion';

import { makeStyles } from '@material-ui/core/styles';
import Backdrop from '@material-ui/core/Backdrop';
import CircularProgress from '@material-ui/core/CircularProgress';
import Cookies from 'js-cookie';

// Layout Blueprints
import { MinimalLayout, MainLayout } from './layout-blueprints';

// Pages
import PageLogin from './pages/PageLogin';
import Page404 from './pages/Page404';
import NotAuthorized from 'pages/NotAuthorized';
import Loading from 'pages/Loading';

import routeDefinitions from './routeDefinitions';
import PageResetPassword from 'pages/PageResetPassword';
import EvidenceScanEntryRoute from 'features/evidence/routes/EvidenceScanEntryRoute';
import PageShareView from 'features/evidence/pages/PageShareView';
import EvidencePortalRoute from './features/evidence/routes/EvidencePortalRoute';
import PageBundleShareView from 'features/evidence/pages/PageBundleShareView';
import { getAccessPermission, getFullPermissions } from 'reducers/PermissionsReducer';
import routePermissionDefination from 'routePermissionDefination';
import { hideSpinner, showSpinner } from 'reducers/UiReducer';
import { notify, notifyPanel } from './reducers/NotifierReducer';
import { registerDateHooks } from './clientHooks.js';
import ServiceName from './features/shared/constants/serviceName';

let noPermissionShown = false;
let registration = false;
let reloadNeeded = false;
const handleReadPermissionCheckForRoutes = (item, userAgency) => {
  const { name, type, agency } = item || {};
  const permission = getFullPermissions(type, name, agency === 'any' ? 'any' : userAgency);
  return permission.Read ? true : false;
};
const checkNecessaryPermissions = (props, userAgency) => {
  const { pathname } = props.location || {};
  if (pathname === '/incidents') {
    const incidentsPermissions = getFullPermissions('rms', 'Incidents', userAgency);
    if (!incidentsPermissions?.Read) return false;
  }
  if (pathname === '/ibrs') {
    const incidentsPermissions = getFullPermissions('rms', 'Incidents', userAgency);
    const permissionForIBRS = getAccessPermission('rms', 'Access IBRS');

    if (!incidentsPermissions?.Read || !permissionForIBRS) {
      return false;
    }
  }
  return true;
};
const ProtectedRoute = ({ component: Component, userAgency, isAuthenticated, ...rest }) => (
  <Route
    {...rest}
    render={(props) => {
      return isAuthenticated === true ? (
        checkNecessaryPermissions(props, userAgency) ? (
          <Component {...props} />
        ) : (
          <Redirect to={{ pathname: '/Main', state: { from: props.location } }} />
        )
      ) : (
        <Redirect to={{ pathname: '/Login', state: { from: props.location } }} />
      );
    }}
  />
);

const LoginRoute = ({ component: Component, isAuthenticated, ...rest }) => {
  return (
    <Route
      {...rest}
      render={(props) =>
        isAuthenticated === true ? (
          <Redirect to={{ pathname: '/home', state: { from: props.location } }} />
        ) : (
          <Component {...props} />
        )
      }
    />
  );
};

const useStyles = makeStyles((theme) => ({
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
    color: '#fff',
  },
}));

const Routes = (props) => {
  const {
    isAuthenticated,
    setWebsocketConnection,
    setUserAuthenticated,
    setUserData,
    circularLoading,
    permissions,
    dictionaryLoaded,
    userData,
    websocket,
  } = props;
  const classes = useStyles();
  const dispatch = useDispatch();
  const location = useLocation();
  const userAgency = useSelector((state) => state.user.userAgency);
  const isLoaded = permissions.loaded;
  const rmsPermission = getAccessPermission('modules', 'RMS', userAgency);
  const [rmsAccessControl, setRmsAccessControl] = useState(true);

  useEffect(() => {
    if (!websocket) return;
    const cdrService = websocket.service('cdr');
    cdrService.get({ Path: 'Settings.Application.RMS', CDRKey: 'AccessControl' })
      .then(data => {
        const item = data?.[0];
        setRmsAccessControl(!item || item.ValueBoolean == null ? true : Boolean(item.ValueBoolean));
      })
      .catch(() => setRmsAccessControl(true));
  }, [websocket]);

  const routePermissionToastShownRef = useRef(new Set());
  useEffect(() => {
    routePermissionToastShownRef.current = new Set();
  }, [location.pathname]);

  useEffect(() => {
    serviceWorker.register({
      onSuccess: () => console.log('service worker registered!'),
      onUpdate: () => {
        sessionStorage.setItem('pending_update', 'true');
      },
    });
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const hasUpdate = sessionStorage.getItem('pending_update');
    if (hasUpdate !== 'true') return;
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (!reg) return;
      const waitingServiceWorker = reg.waiting;
      if (!waitingServiceWorker) return;
      waitingServiceWorker.postMessage({ type: 'SKIP_WAITING' });
      waitingServiceWorker.onstatechange = (event) => {
        if (event.target.state === 'activated') {
          sessionStorage.removeItem('pending_update');
          window.location.reload();
        }
      };
    });
  }, [isAuthenticated]);

  useEffect(() => {
    async function establishConnection() {
      const client = feathers();
      client.configure(
        socketio(
          io(window.__RUNTIME_CONFIG__.REACT_APP_API_URL, {
            transports: ['websocket', 'polling'],
            perMessageDeflate: false,
            timeout: 20000,
            requestTimeout: 20000,
          })
        )
      );
      client.configure(auth({ storageKey: 'auth' }));
      try {
        const auth = Cookies.get('auth');
        if (auth) {
          window.localStorage.setItem('auth', auth);
          Cookies.remove('auth');
        }
        const user = await client.reAuthenticate();
        setWebsocketConnection(client);
        // register date-transform hooks across all relevant services
        registerDateHooks(client, [
          ServiceName.rms_evidence_detail,
          ServiceName.rms_evidence_storage,
          ServiceName.rms_evidence_custody,
          ServiceName.CadAddressCoordinates,
          ServiceName.CadAddressPrePlan,
          ServiceName.CadAddressPrePlanInspections,
          ServiceName.CadSearchAlerts,
          ServiceName.CadSearchPeople,
          ServiceName.RecordPersonCredentials,
          ServiceName.CadPersonDnaSamples,
          ServiceName.CadSearchPeopleEmployment,
        ]);
        getRestClient();
        // setUserData(user);
        // dispatch(setUserAgency(user?.user?.AgencyID || ''));
        dispatch(submitLoginForm(null, null, user));
        // getUserClearanceLevel(user.user.ptsClearanceLevelID);
        // setUserAuthenticated(true);
      } catch (error) {
        if (error.code === 401) {
          setUserAuthenticated(false);
          setUserData(null);
          setWebsocketConnection(client);
        }
        setWebsocketConnection(client);
      }

      return () => client.disconnect();
    }

    if (!websocket) {
      establishConnection();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websocket, setWebsocketConnection, setUserAuthenticated]);

  const refreshToken = async () => {
    try {
      props.showSpinner();
      const currentToken = localStorage.getItem('auth');
      const response = await websocket.service('authentication').create({
        strategy: 'jwt',
        accessToken: currentToken,
        isRenewToken: true,
        AppName: 'RMS',
      });
      localStorage.setItem('auth', response.accessToken);
      websocket.set('authentication', response);
      setUserData(response);
      setUserAuthenticated(true);
      props.notify('Session extended!', 'success');
    } catch (error) {
      console.error('Refresh Failed', error);
      setUserAuthenticated(false);
    } finally {
      props.hideSpinner();
    }
  };

  const showWarning = (expiryTime) => {
    const expiryTimeText = new Date(expiryTime).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });

    props.notifyPanel(
      {
        title: 'Session Expiring Soon!',
        message: `Your session will expire at ${expiryTimeText}. Click CONTINUE to stay signed in.`,
        position: 'tc',
        level: 'warning',
        autoDismiss: props.sessionWarningMinutes * 60,
        action: {
          label: 'CONTINUE',
          callback: refreshToken,
        },
      },
      'warning'
    );
  };

  React.useEffect(() => {
    if (!isAuthenticated || !userData || !props.sessionWarningEnabled) return;
    const payload = userData?.authentication?.payload || userData?.payload;
    const exp = payload?.exp;
    if (!exp) return;
    const expiryTime = exp * 1000;
    const warningSeconds = Number(props.sessionWarningMinutes) * 60;
    const now = Date.now();
    const delay = expiryTime - now - warningSeconds * 1000;

    if (delay <= 0) {
      if (now < expiryTime) {
        showWarning(expiryTime);
      }
      return;
    }
    const timer = setTimeout(() => {
      showWarning(expiryTime);
    }, delay);
    return () => clearTimeout(timer);
  }, [isAuthenticated, userData, props.sessionWarningEnabled]);

  const pageVariants = {
    initial: {
      opacity: 0,
      scale: 0.99,
      width: '100%',
      height: '100%',
    },
    in: {
      opacity: 1,
      scale: 1,
    },
    out: {
      opacity: 0,
      scale: 1.01,
    },
  };

  const pageTransition = {
    type: 'tween',
    ease: 'anticipate',
    duration: 0.4,
  };

  const SuspenseLoading = () => {
    return (
      <div className="d-flex align-items-center flex-column vh-100 justify-content-center text-center py-3">
        <div className="d-flex align-items-center flex-column px-4">
          <ClimbingBoxLoader color={'#0153a3'} loading={true} />
        </div>
        <div className="text-muted font-size-xl text-center pt-3">
          Please wait while we load the view...
        </div>
      </div>
    );
  };

  if (!dictionaryLoaded && isAuthenticated)
    return (
      <div
        style={{
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}>
        <h4 className="pb-5"> Loading</h4>
      </div>
    );
  return (
    <Suspense fallback={<SuspenseLoading />}>
      <Switch>
        <Redirect exact from="/" to="/Login" />
        <Route path={['/Login']}>
          <MinimalLayout>
            <motion.div
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}>
              <LoginRoute path="/Login" component={PageLogin} isAuthenticated={isAuthenticated} />
            </motion.div>
          </MinimalLayout>
        </Route>
        <Route path={['/Reset-password']}>
          <MinimalLayout>
            <PageResetPassword isAuthenticated={isAuthenticated} />
          </MinimalLayout>
        </Route>

        <Route path={['/share/:token']}>
          <MinimalLayout>
            <PageShareView />
          </MinimalLayout>
        </Route>
        <Route path={['/mobile-scan']}>
          <MinimalLayout>
            <EvidenceScanEntryRoute />
          </MinimalLayout>
        </Route>

        <Route path="/portal">
          <MinimalLayout>
            <EvidencePortalRoute />
          </MinimalLayout>
        </Route>
        
        <Route path={['/share-bundle/:token']}>
          <MinimalLayout>
            <PageBundleShareView />
          </MinimalLayout>
        </Route>

        {routeDefinitions.map((route, key) => {
          let Layout = isLoaded ? route.layout : MinimalLayout;
          let component = route.component;
          const isLazy = route.component._payload;

          if (isLoaded && ( !rmsPermission || !rmsAccessControl )) {
            component = NotAuthorized;
            Layout = MinimalLayout;
            if (!noPermissionShown) {
              noPermissionShown = true;
              dispatch(
                handleError({
                  code: 440,
                  message: "You don't have enough permission to Login!",
                })
              );
              dispatch(logOut());
            }
          }

          const normalizedRouteKey = route.url === '/' ? route.url : route.url.replace(/\/+$/, '');
          if (routePermissionDefination[normalizedRouteKey]) {
            const visitedKeys = new Set();
            let permissionAllowed = true;
            let permissionKey = normalizedRouteKey;
            while (permissionKey) {
              if (visitedKeys.has(permissionKey)) {
                permissionAllowed = false;
                break;
              }
              visitedKeys.add(permissionKey);
              const permissionNode = routePermissionDefination[permissionKey];
              const { name, type, parent } = permissionNode || {};
              const hasPermissionMeta = Boolean(name && type);

              if (hasPermissionMeta) {
                if (!handleReadPermissionCheckForRoutes(permissionNode, userAgency)) {
                  permissionAllowed = false;
                  break;
                }
              }
              if (!parent) break;

              const normalizedParentKey =
                parent === '/'
                  ? parent
                  : (parent.startsWith('/') ? parent : `/${parent}`).replace(/\/+$/, '');
              if (!routePermissionDefination[normalizedParentKey]) break;
              permissionKey = normalizedParentKey;
            }
            if (!permissionAllowed) {
              component = Page404;
              Layout = MainLayout;
              const isCurrentRoute = Boolean(
                matchPath(location.pathname, {
                  path: route.url,
                  exact: true,
                  strict: false,
                })
              );
              if (isCurrentRoute && !routePermissionToastShownRef.current.has(route.url)) {
                routePermissionToastShownRef.current.add(route.url);
                props.notify("You don't have permission to access this page.", 'error');
              }
            }
          }

          const componentRoute = (
            <>
              <Backdrop className={classes.backdrop} open={circularLoading}>
                <CircularProgress color="inherit" />
              </Backdrop>
              <Layout>
                <motion.div
                  initial="initial"
                  animate="in"
                  exit="out"
                  variants={pageVariants}
                  transition={pageTransition}>
                  {/*TODO: Change this back to protected route for production*/}
                  <ProtectedRoute
                    path={route.url}
                    component={component}
                    isAuthenticated={isAuthenticated}
                    userAgency={userAgency}
                  />
                </motion.div>
              </Layout>
            </>
          );

          if (isLazy) {
            return (
              <Route path={[route.url]} key={key}>
                <Suspense fallback={<SuspenseLoading />}>{componentRoute}</Suspense>
              </Route>
            );
          } else {
            return (
              <Route path={[route.url]} key={key}>
                {componentRoute}
              </Route>
            );
          }
        })}

        <Route>
          <MainLayout>
            <motion.div
              initial="initial"
              animate="in"
              exit="out"
              variants={pageVariants}
              transition={pageTransition}>
              <ProtectedRoute component={Page404} isAuthenticated={isAuthenticated} />
            </motion.div>
          </MainLayout>
        </Route>
      </Switch>
    </Suspense>
  );
};

const mapStateToProps = (state) => ({
  network: state.offline,
  isAuthenticated: state.user.isAuthenticated,
  circularLoading: state.uiMenu.circularLoading,
  permissions: state.permissions,
  loggedInOrNot: state.user.loggedInOrNot,
  dictionaryLoaded: Object.keys(state.dictionary).length > 0,
  userData: state.user.userData,
  websocket: state.websocket.websocket,
  sessionWarningEnabled: state.settings.sessionWarningEnabled,
  sessionWarningMinutes: state.settings.sessionWarningMinutes,
});

export default connect(mapStateToProps, {
  setWebsocketConnection,
  setUserAuthenticated,
  setUserData,
  notifyPanel,
  notify,
  showSpinner,
  hideSpinner,
})(Routes);
